import * as THREE from "three";
import { TankData, ObstacleData } from "./stores/useRocketanks";

export type ArenaShape = "square" | "circle" | "cross" | "corridor" | "maze";
export type TerrainTheme = "industrial" | "desert" | "arctic" | "forest" | "volcanic" | "cyber";

// ============================================
// BALANCEAMENTO DE DIFICULDADE
// ============================================

/**
 * Curva de dificuldade logarítmica
 * Aumenta rapidamente no início, depois estabiliza
 */
function difficultyMultiplier(wave: number, base: number = 1.15): number {
  // Curva: rápida no início, lenta depois
  // Waves 1-5: aumento moderado
  // Waves 6-15: aumento lento
  // Waves 16+: quase estável
  if (wave <= 5) {
    return 1 + (wave - 1) * 0.12; // +12% por wave
  } else if (wave <= 15) {
    return 1.48 + (wave - 5) * 0.06; // +6% por wave
  } else {
    return 2.08 + (wave - 15) * 0.03; // +3% por wave
  }
}

/**
 * Calcula a dificuldade baseada no desempenho do jogador
 * Retorna um multiplicador entre 0.8 e 1.2
 */
export function adaptiveDifficultyMultiplier(
  playerHealth: number,
  maxHealth: number,
  killsThisWave: number,
  totalEnemies: number,
  timeTaken: number // em segundos
): number {
  let modifier = 1.0;
  
  // Se jogador está com pouca vida, reduzir dificuldade
  const healthPercent = playerHealth / maxHealth;
  if (healthPercent < 0.3) {
    modifier -= 0.15; // -15% se abaixo de 30% vida
  } else if (healthPercent < 0.5) {
    modifier -= 0.08; // -8% se abaixo de 50% vida
  } else if (healthPercent > 0.9) {
    modifier += 0.08; // +8% se acima de 90% vida
  }
  
  // Se matou muitos inimigos rápido, aumentar dificuldade
  const killRate = totalEnemies > 0 ? killsThisWave / totalEnemies : 0;
  if (killRate > 0.8 && timeTaken < 30) {
    modifier += 0.1; // +10% se dominou a wave
  } else if (killRate < 0.3 && timeTaken > 60) {
    modifier -= 0.1; // -10% se está tendo dificuldade
  }
  
  // Limitar entre 0.8 e 1.2
  return Math.max(0.8, Math.min(1.2, modifier));
}

// ============================================
// CONFIGURAÇÃO DE TERRENOS
// ============================================

/** Comportamento das partículas ambientais de cada tema. */
export type TerrainParticles = "embers" | "snow" | "fireflies" | "dust" | "sparks" | "motes";

/**
 * Atmosfera visual do tema: cores das luzes da cena, textura do chão e
 * partículas ambientais. Tudo aqui tem custo de render constante — trocar
 * cores de luz e textura não adiciona draw calls nem passes.
 */
export interface TerrainAtmosphere {
  /** Cor do céu (hemisphere light). */
  skyColor: string;
  /** Cor refletida do chão (hemisphere light). */
  groundColor: string;
  /** Cor da luz direcional principal (sol). */
  sunColor: string;
  /** Cor da luz de preenchimento. */
  fillColor: string;
  /** Cor da luz de contorno (rim). */
  rimColor: string;
  /** Textura do chão da arena. */
  floorTexture: string;
  particles: TerrainParticles;
  particleColor: string;
}

export interface TerrainConfig {
  theme: TerrainTheme;
  name: string;
  floorColor: string;
  wallColor: string;
  accentColor: string;
  fogColor: string;
  ambientColor: string;
  atmosphere?: TerrainAtmosphere;
}

/** Atmosfera neutra (industrial) — fallback para configs sem atmosfera própria. */
export const DEFAULT_ATMOSPHERE: TerrainAtmosphere = {
  skyColor: "#87CEEB",
  groundColor: "#3d2817",
  sunColor: "#fff5e6",
  fillColor: "#6080ff",
  rimColor: "#ffd0a0",
  floorTexture: "/textures/asphalt.png",
  particles: "motes",
  particleColor: "#66ffe0",
};

export const TERRAIN_THEMES: Record<TerrainTheme, TerrainConfig> = {
  industrial: {
    theme: "industrial",
    name: "Industrial",
    floorColor: "#445566",
    wallColor: "#223344",
    accentColor: "#00ffcc",
    fogColor: "#0a0a1a",
    ambientColor: "#334455",
    atmosphere: DEFAULT_ATMOSPHERE,
  },
  desert: {
    theme: "desert",
    name: "Deserto",
    floorColor: "#c4a574",
    wallColor: "#8b7355",
    accentColor: "#ff9933",
    fogColor: "#2a1f14",
    ambientColor: "#554433",
    atmosphere: {
      skyColor: "#ffd9a6",
      groundColor: "#6b4a26",
      sunColor: "#ffe3b0",
      fillColor: "#8fa3d9",
      rimColor: "#ffc98a",
      floorTexture: "/textures/sand.jpg",
      particles: "dust",
      particleColor: "#e8c890",
    },
  },
  arctic: {
    theme: "arctic",
    name: "Ártico",
    floorColor: "#a8c8d8",
    wallColor: "#5588aa",
    accentColor: "#66ccff",
    fogColor: "#1a2a3a",
    ambientColor: "#88aacc",
    atmosphere: {
      skyColor: "#cfe8ff",
      groundColor: "#3a5a7a",
      sunColor: "#eaf6ff",
      fillColor: "#7f9fd9",
      rimColor: "#d6f0ff",
      floorTexture: "/textures/asphalt.png",
      particles: "snow",
      particleColor: "#ffffff",
    },
  },
  forest: {
    theme: "forest",
    name: "Floresta",
    floorColor: "#4a6b35",
    wallColor: "#2d4420",
    accentColor: "#33ff66",
    fogColor: "#0a1a0a",
    ambientColor: "#446633",
    atmosphere: {
      skyColor: "#a8d8a0",
      groundColor: "#1d3315",
      sunColor: "#fff2cc",
      fillColor: "#4d8a5d",
      rimColor: "#d0e8a0",
      floorTexture: "/textures/grass.png",
      particles: "fireflies",
      particleColor: "#b8ff66",
    },
  },
  volcanic: {
    theme: "volcanic",
    name: "Vulcânico",
    floorColor: "#3a2a2a",
    wallColor: "#2a1a1a",
    accentColor: "#ff3300",
    fogColor: "#1a0a0a",
    ambientColor: "#442222",
    atmosphere: {
      skyColor: "#7a3322",
      groundColor: "#140a08",
      sunColor: "#ffa066",
      fillColor: "#803022",
      rimColor: "#ffb380",
      floorTexture: "/textures/asphalt.png",
      particles: "embers",
      particleColor: "#ff6633",
    },
  },
  cyber: {
    theme: "cyber",
    name: "Cyber",
    floorColor: "#1a1a2e",
    wallColor: "#0f0f1a",
    accentColor: "#ff00ff",
    fogColor: "#050510",
    ambientColor: "#221133",
    atmosphere: {
      skyColor: "#3a2a6a",
      groundColor: "#0d0d1a",
      sunColor: "#e0ccff",
      fillColor: "#ff44ff",
      rimColor: "#00ffff",
      floorTexture: "/textures/asphalt.png",
      particles: "sparks",
      particleColor: "#ff66ff",
    },
  },
};

export interface WaveConfig {
  wave: number;
  arenaSize: number;
  arenaShape: ArenaShape;
  terrain: TerrainConfig;
  enemyCount: number;
  enemies: EnemyConfig[];
  obstaclesDensity: number;
  hasDestructibles: boolean;
}

export interface EnemyConfig {
  type: "normal" | "heavy" | "fast" | "sniper" | "boss";
  health: number;
  speed: number;
  damage: number;
  fireRate: number;
  shotCount: number;
  size: number;
  color: string;
  aiLevel: number;
  /** Modificador "volátil": deixa uma explosão ao morrer. */
  explodesOnDeath?: boolean;
}

const ENEMY_COLORS = ["#ff3366", "#ff9933", "#9933ff", "#33ff99", "#ff6600", "#cc00ff", "#00ccff", "#ffcc00"];

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function getTerrainForWave(wave: number): TerrainConfig {
  const themes: TerrainTheme[] = ["industrial", "desert", "arctic", "forest", "volcanic", "cyber"];
  const themeIndex = Math.floor((wave - 1) / 5) % themes.length;
  return TERRAIN_THEMES[themes[themeIndex]];
}

/**
 * Ajustes estruturais da onda no modo infinito.
 *
 * Ficam opcionais para que todos os chamadores antigos (multiplayer, história,
 * battle royale) continuem produzindo exatamente a mesma onda de antes.
 */
export interface WaveShaping {
  /** Posição da onda no bloco de 10. */
  kind?: "normal" | "elite" | "boss";
  /** Modificador do bloco atual — muda o COMO, não o quanto. */
  worldModifier?: "none" | "swarm" | "juggernaut" | "sharpshooter" | "blitz" | "volatile";
  /**
   * Multiplicador vindo de `adaptiveDifficultyMultiplier` (0.8 a 1.2). O jogo
   * cede um pouco para quem está apanhando e aperta com quem está dominando.
   */
  adaptive?: number;
}

export function generateWaveConfig(
  wave: number,
  overrideTerrain?: TerrainConfig,
  overrideSeed?: number,
  shaping?: WaveShaping,
): WaveConfig {
  const random = seededRandom(overrideSeed !== undefined ? overrideSeed : wave * 1337);

  const kind = shaping?.kind ?? "normal";
  const worldModifier = shaping?.worldModifier ?? "none";
  // Teto em 1,35: 1,2 vem do desempenho e o resto do peso da build.
  const adaptive = Math.max(0.8, Math.min(1.35, shaping?.adaptive ?? 1));
  const isBossWave = kind === "boss";
  const isEliteWave = kind === "elite";

  const baseSize = 32 + Math.min(wave * 2, 38);
  const sizeVariation = (random() - 0.5) * 6;
  // A onda de chefe precisa de espaço: o padrão de ataque tem de ser legível
  // e o jogador tem de ter para onde recuar.
  const bossRoom = isBossWave ? 8 : 0;
  const arenaSize = Math.max(28, Math.min(70, baseSize + sizeVariation + bossRoom));

  const shapes: ArenaShape[] = ["square", "square", "circle", "cross", "corridor", "maze"];
  const maxShapeIndex = Math.min(shapes.length - 1, Math.floor(wave / 4));
  const shapeIndex = Math.floor(random() * (maxShapeIndex + 1));
  // Corredor e labirinto sufocam a arena do chefe — nelas o jogador morre
  // encurralado sem chance de ler o ataque, não por ter jogado mal.
  const arenaShape = isBossWave ? (random() > 0.5 ? "square" : "circle") : shapes[shapeIndex];

  const terrain = overrideTerrain || getTerrainForWave(wave);

  let enemyCount: number;
  if (wave === 1) {
    enemyCount = 2;
  } else if (wave === 2) {
    enemyCount = 2 + Math.floor(random() * 2);
  } else if (wave <= 5) {
    enemyCount = 3 + Math.floor(random() * 2);
  } else if (wave <= 10) {
    // Evita pico abrupto entre wave 9/10.
    enemyCount = 4 + Math.floor(random() * 2);
  } else {
    const baseEnemies = 4 + Math.floor((wave - 10) * 0.35);
    enemyCount = Math.min(8, baseEnemies + Math.floor(random() * 2));
  }

  // A escolta do chefe é pequena de propósito: o chefe é o evento, e oito
  // tanques em volta transformariam a luta em ruído.
  if (isBossWave) enemyCount = Math.min(4, Math.max(2, Math.floor(enemyCount / 2)));
  else if (isEliteWave) enemyCount += 1;

  if (worldModifier === "swarm") enemyCount += 3;
  else if (worldModifier === "juggernaut") enemyCount = Math.max(2, enemyCount - 2);
  enemyCount = Math.max(1, Math.min(12, enemyCount));

  const enemies: EnemyConfig[] = [];
  // AI Level: começa em 0.25, chega a 0.9 na wave 20
  const aiLevel = Math.min(
    0.95,
    (0.25 + wave * 0.035) * (isEliteWave ? 1.12 : 1) * (isBossWave ? 1.08 : 1),
  );

  // Multiplicador de dificuldade baseado na curva logarítmica
  const diffMult = difficultyMultiplier(wave);

  for (let i = 0; i < enemyCount; i++) {
    const enemyRoll = random();
    
    let type: EnemyConfig["type"] = "normal";
    let health: number;
    let speed: number;
    let damage: number;
    let fireRate: number;
    let shotCount = 1;
    let size = 1;
    
    // Base stats com curva suave
    // Wave 1: fácil para tutorial
    // Wave 2-5: aprendizado
    // Wave 6-10: desafio moderado
    // Wave 11+: desafio real
    
    if (wave === 1) {
      // Wave tutorial - muito fácil
      health = 35;
      speed = 4;
      damage = 8;
      fireRate = 3500;
    } else if (wave === 2) {
      health = 45;
      speed = 4.5;
      damage = 10;
      fireRate = 3200;
    } else if (wave <= 5) {
      // Waves de aprendizado
      health = 45 + (wave - 2) * 8;
      speed = 4.5 + (wave - 2) * 0.4;
      damage = 10 + (wave - 2) * 2;
      fireRate = 3200 - (wave - 2) * 150;
    } else if (wave <= 10) {
      // Desafio moderado (suavizado para evitar gargalo na 9/10)
      health = 64 + (wave - 5) * 8;
      speed = 5.6 + (wave - 5) * 0.25;
      damage = 14 + (wave - 5) * 1.6;
      fireRate = 2800 - (wave - 5) * 120;
    } else {
      // Desafio real com escalada contínua sem salto brusco.
      const baseHealth = 108;
      const baseSpeed = 6.9;
      const baseDamage = 22;
      const baseFireRate = 2200;
      
      health = baseHealth * (0.55 + diffMult * 0.35);
      speed = baseSpeed + (wave - 10) * 0.15; // Velocidade aumenta lentamente
      damage = baseDamage * (0.5 + diffMult * 0.25);
      fireRate = Math.max(800, baseFireRate - (wave - 10) * 40);
    }
    
    // Tipos especiais com chances progressivas
    const bossChance = wave >= 12 ? Math.min(0.18, 0.02 + (wave - 12) * 0.008) : 0;
    const heavyChance = wave >= 6 ? Math.min(0.32, 0.12 + (wave - 6) * 0.012) : 0;
    const fastChance = wave >= 3 ? Math.min(0.34, 0.18 + (wave - 3) * 0.008) : 0;
    const sniperChance = wave >= 8 ? Math.min(0.22, 0.08 + (wave - 8) * 0.01) : 0;
    
    // Apenas 1 boss por wave, no máximo
    const bossAlreadyExists = enemies.some(e => e.type === "boss");

    // Na onda de chefe o primeiro inimigo É o chefe, sempre — o anúncio feito
    // três ondas antes precisa se cumprir, e não pode depender de um sorteio.
    if (isBossWave && i === 0) {
      type = "boss";
      // Mais forte que o "boss" avulso das ondas normais: este é o evento que
      // fecha o bloco de dez.
      health = 320 + wave * 20;
      speed = 3.8 + Math.min(wave * 0.1, 2);
      damage = 32 + wave * 1.6;
      fireRate = 1800;
      shotCount = 3 + Math.floor(wave / 10);
      size = 2;
    } else if (worldModifier === "sharpshooter" && !bossAlreadyExists && random() > 0.45) {
      // Bloco de atiradores: a arena inteira vira um problema de cobertura.
      type = "sniper";
      health = 50 + wave * 2.5;
      speed = 4.5;
      damage = 35 + wave * 1.8;
      fireRate = 4000;
      shotCount = 1;
      size = 1;
    } else if (!bossAlreadyExists && !isBossWave && wave >= 7 && enemyRoll > (1 - bossChance)) {
      type = "boss";
      health = 200 + wave * 12;
      speed = 3.5 + Math.min(wave * 0.1, 1.5);
      damage = 30 + wave * 1.5;
      fireRate = 2000;
      shotCount = 2 + Math.floor(wave / 10);
      size = 1.7;
    } else if (wave >= 5 && enemyRoll > (1 - heavyChance)) {
      type = "heavy";
      health = 100 + wave * 6;
      speed = 4 + Math.min(wave * 0.1, 1.5);
      damage = 20 + wave * 1.5;
      fireRate = 3000;
      shotCount = 2;
      size = 1.35;
    } else if (wave >= 3 && enemyRoll > (1 - fastChance)) {
      type = "fast";
      health = 40 + wave * 2.5;
      speed = 9 + Math.min(wave * 0.35, 4);
      damage = 8 + wave * 0.8;
      fireRate = 2400;
      shotCount = 1;
      size = 0.8;
    } else if (wave >= 6 && enemyRoll > (1 - sniperChance)) {
      type = "sniper";
      health = 50 + wave * 2.5;
      speed = 4.5;
      damage = 35 + wave * 1.8;
      fireRate = 4000;
      shotCount = 1;
      size = 1;
    }
    
    // ---- Modificador de bloco ----
    // Escala por COMPORTAMENTO, não por número: depois da onda 20 é isto que
    // mantém a corrida interessante, em vez de transformar cada inimigo numa
    // esponja de dano com 4.000 de vida.
    if (type !== "boss") {
      if (worldModifier === "swarm") {
        health *= 0.6;
        speed *= 1.1;
      } else if (worldModifier === "juggernaut") {
        health *= 1.75;
        speed *= 0.85;
        size *= 1.15;
      } else if (worldModifier === "blitz") {
        speed *= 1.3;
        fireRate *= 0.85;
      }
    }
    if (isEliteWave) {
      health *= 1.2;
      damage *= 1.1;
    }

    // Dificuldade adaptativa: até aqui era código morto — exportada e nunca
    // chamada. Mexe só em vida e dano, os dois eixos que o jogador sente sem
    // que a onda mude de forma no meio da corrida.
    health *= adaptive;
    damage *= adaptive;

    enemies.push({
      type,
      health: Math.max(1, Math.floor(health)),
      speed: Math.round(speed * 100) / 100, // Arredondar para 2 casas
      damage: Math.max(1, Math.floor(damage)),
      fireRate: Math.max(800, Math.floor(fireRate)),
      shotCount,
      size,
      color: ENEMY_COLORS[i % ENEMY_COLORS.length],
      aiLevel,
      explodesOnDeath: worldModifier === "volatile" && type !== "boss",
    });
  }

  const obstaclesDensity = 0.3 + random() * 0.4;
  const hasDestructibles = random() > 0.3;

  return {
    wave,
    arenaSize,
    arenaShape,
    terrain,
    enemyCount,
    enemies,
    obstaclesDensity,
    hasDestructibles,
  };
}

export function generateArenaObstacles(config: WaveConfig, random: () => number): ObstacleData[] {
  const obstacles: ObstacleData[] = [];
  const { arenaSize, arenaShape, obstaclesDensity, hasDestructibles } = config;
  const boundary = arenaSize / 2 - 3;
  
  const gridSpacing = 4;
  const gridCount = Math.floor(arenaSize / gridSpacing) - 2;
  const offset = ((gridCount - 1) * gridSpacing) / 2;
  
  const spawnZones = [
    { x: 0, z: 0, radius: 6 },
    { x: -boundary + 3, z: -boundary + 3, radius: 4 },
    { x: boundary - 3, z: -boundary + 3, radius: 4 },
    { x: -boundary + 3, z: boundary - 3, radius: 4 },
    { x: boundary - 3, z: boundary - 3, radius: 4 },
    { x: 0, z: -boundary + 3, radius: 4 },
    { x: 0, z: boundary - 3, radius: 4 },
    { x: -boundary + 3, z: 0, radius: 4 },
    { x: boundary - 3, z: 0, radius: 4 },
  ];
  
  const isInSpawnZone = (x: number, z: number) => {
    return spawnZones.some((zone) => {
      const dx = x - zone.x;
      const dz = z - zone.z;
      return Math.sqrt(dx * dx + dz * dz) < zone.radius;
    });
  };
  
  const isInArena = (x: number, z: number) => {
    if (arenaShape === "circle") {
      return Math.sqrt(x * x + z * z) < boundary - 2;
    } else if (arenaShape === "cross") {
      const armWidth = arenaSize / 4;
      return Math.abs(x) < armWidth || Math.abs(z) < armWidth;
    } else if (arenaShape === "corridor") {
      return Math.abs(x) < arenaSize / 6 || Math.abs(z) < boundary;
    }
    return Math.abs(x) < boundary && Math.abs(z) < boundary;
  };
  
  for (let i = 0; i < gridCount; i++) {
    for (let j = 0; j < gridCount; j++) {
      const x = i * gridSpacing - offset;
      const z = j * gridSpacing - offset;
      
      if (isInSpawnZone(x, z)) continue;
      if (!isInArena(x, z)) continue;
      
      const shouldPlace = random() < obstaclesDensity;
      if (!shouldPlace) continue;
      
      const isIndestructible = i % 2 === 1 && j % 2 === 1;
      const isDestructible = hasDestructibles && !isIndestructible && random() > 0.5;
      
      if (isIndestructible || isDestructible) {
        obstacles.push({
          id: `obs-${i}-${j}`,
          position: new THREE.Vector3(x, 0, z),
          size: new THREE.Vector3(2, isIndestructible ? 2.5 : 1.5, 2),
          type: isIndestructible ? "indestructible" : "destructible",
          health: isIndestructible ? 999 : 2,
          maxHealth: isIndestructible ? 999 : 2,
        });
      }
    }
  }
  
  if (arenaShape === "maze" && config.wave >= 3) {
    const wallCount = 3 + Math.floor(config.wave / 2);
    for (let w = 0; w < wallCount; w++) {
      const isHorizontal = random() > 0.5;
      const pos = (random() - 0.5) * (boundary * 1.2);
      const length = 4 + Math.floor(random() * 4);
      
      for (let seg = 0; seg < length; seg++) {
        const x = isHorizontal ? pos : -boundary / 2 + seg * 3;
        const z = isHorizontal ? -boundary / 2 + seg * 3 : pos;
        
        if (isInSpawnZone(x, z)) continue;
        if (Math.abs(x) > boundary - 2 || Math.abs(z) > boundary - 2) continue;
        
        obstacles.push({
          id: `wall-${w}-${seg}`,
          position: new THREE.Vector3(x, 0, z),
          size: new THREE.Vector3(2.5, 2, 2.5),
          type: "indestructible",
          health: 999,
          maxHealth: 999,
        });
      }
    }
  }
  
  return obstacles;
}

export interface GridObstacleConfig {
  arenaSize: number;
  /** Espaçamento entre células da grade. */
  gridSpacing: number;
  /** Distância da borda da arena onde blocos não são colocados. */
  boundaryMargin: number;
  /** Prefixo dos ids gerados (ex.: "br-obs" → "br-obs-3-4"). */
  idPrefix: string;
  /** Células dentro da zona segura (spawns) não recebem blocos. */
  isInSafeZone: (x: number, z: number) => boolean;
  /** Probabilidade [0..1] de bloco destrutível nas células livres. 0 = só pilares. */
  destructibleDensity: number;
  destructibleHealth: number;
  /** Lado (x/z) dos blocos. */
  blockFootprint: number;
  indestructibleHeight: number;
  destructibleHeight: number;
  /** Fonte de aleatoriedade (default Math.random; injete seededRandom para determinismo). */
  random?: () => number;
}

/**
 * Gera obstáculos em grade estilo Bomberman: pilares indestrutíveis nas
 * posições ímpares e blocos destrutíveis aleatórios nas demais células.
 * Usado pelo Battle Royale e pela arena GenAI (antes duplicado em cada modo).
 */
export function generateGridObstacles(cfg: GridObstacleConfig): ObstacleData[] {
  const rand = cfg.random ?? Math.random;
  const obstacles: ObstacleData[] = [];
  const boundary = cfg.arenaSize / 2 - cfg.boundaryMargin;
  const gridCount = Math.floor(cfg.arenaSize / cfg.gridSpacing);
  const offset = ((gridCount - 1) * cfg.gridSpacing) / 2;

  for (let i = 0; i < gridCount; i++) {
    for (let j = 0; j < gridCount; j++) {
      const x = i * cfg.gridSpacing - offset;
      const z = j * cfg.gridSpacing - offset;

      if (Math.abs(x) > boundary || Math.abs(z) > boundary) continue;
      if (cfg.isInSafeZone(x, z)) continue;

      const isIndestructible = i % 2 === 1 && j % 2 === 1;
      const placeDestructible = !isIndestructible && rand() < cfg.destructibleDensity;
      if (!isIndestructible && !placeDestructible) continue;

      obstacles.push({
        id: `${cfg.idPrefix}-${i}-${j}`,
        position: new THREE.Vector3(x, 0, z),
        size: new THREE.Vector3(
          cfg.blockFootprint,
          isIndestructible ? cfg.indestructibleHeight : cfg.destructibleHeight,
          cfg.blockFootprint
        ),
        type: isIndestructible ? "indestructible" : "destructible",
        health: isIndestructible ? 999 : cfg.destructibleHealth,
        maxHealth: isIndestructible ? 999 : cfg.destructibleHealth,
      });
    }
  }

  return obstacles;
}

export function generateEnemyTanks(config: WaveConfig, obstacles: ObstacleData[], random: () => number): TankData[] {
  const enemies: TankData[] = [];
  const boundary = config.arenaSize / 2 - 4;
  const spawnedEnemyFootprints: Array<{ pos: THREE.Vector3; radius: number }> = [];
  
  const spawnPositions = [
    new THREE.Vector3(-boundary, 0, -boundary),
    new THREE.Vector3(boundary, 0, -boundary),
    new THREE.Vector3(-boundary, 0, boundary),
    new THREE.Vector3(boundary, 0, boundary),
    new THREE.Vector3(0, 0, -boundary),
    new THREE.Vector3(0, 0, boundary),
    new THREE.Vector3(-boundary, 0, 0),
    new THREE.Vector3(boundary, 0, 0),
  ];
  
  const isPositionBlocked = (pos: THREE.Vector3, checkRadius: number): boolean => {
    for (const obs of obstacles) {
      const dx = Math.abs(pos.x - obs.position.x);
      const dz = Math.abs(pos.z - obs.position.z);
      const halfWidth = obs.size.x / 2 + checkRadius;
      const halfDepth = obs.size.z / 2 + checkRadius;
      if (dx < halfWidth && dz < halfDepth) {
        return true;
      }
    }
    for (const footprint of spawnedEnemyFootprints) {
      const dx = pos.x - footprint.pos.x;
      const dz = pos.z - footprint.pos.z;
      const minDist = checkRadius + footprint.radius;
      if (dx * dx + dz * dz < minDist * minDist) {
        return true;
      }
    }
    return false;
  };
  
  const isInArena = (pos: THREE.Vector3): boolean => {
    const { arenaShape, arenaSize } = config;
    const limit = arenaSize / 2 - 2;
    
    if (arenaShape === "circle") {
      return Math.sqrt(pos.x * pos.x + pos.z * pos.z) < limit;
    } else if (arenaShape === "cross") {
      const armWidth = arenaSize / 4;
      return Math.abs(pos.x) < armWidth || Math.abs(pos.z) < armWidth;
    } else if (arenaShape === "corridor") {
      return Math.abs(pos.x) < arenaSize / 6 || Math.abs(pos.z) < limit;
    }
    return Math.abs(pos.x) < limit && Math.abs(pos.z) < limit;
  };
  
  const findSafeSpawn = (
    basePos: THREE.Vector3,
    checkRadius: number,
    attempts: number = 28
  ): THREE.Vector3 | null => {
    for (let a = 0; a < attempts; a++) {
      const testPos = basePos.clone();
      testPos.x += (random() - 0.5) * 8;
      testPos.z += (random() - 0.5) * 8;
      
      if (!isPositionBlocked(testPos, checkRadius) && isInArena(testPos)) {
        return testPos;
      }
    }

    // Fallback robusto: varredura em anéis para evitar spawn preso em bloco.
    const maxRadius = Math.max(6, boundary * 0.88);
    const radialSteps = 16;
    const ringCount = 12;
    for (let ring = 0; ring < ringCount; ring++) {
      const radius = 2 + (ring / Math.max(1, ringCount - 1)) * maxRadius;
      for (let step = 0; step < radialSteps; step++) {
        const angle = (step / radialSteps) * Math.PI * 2 + ring * 0.12;
        const candidate = new THREE.Vector3(
          Math.cos(angle) * radius,
          0,
          Math.sin(angle) * radius
        );
        if (!isInArena(candidate)) continue;
        if (!isPositionBlocked(candidate, checkRadius)) {
          return candidate;
        }
      }
    }

    return null;
  };
  
  for (let i = 0; i < config.enemies.length; i++) {
    const enemyConfig = config.enemies[i];
    const posIndex = i % spawnPositions.length;
    const basePos = spawnPositions[posIndex];
    const enemyRadius = Math.max(1.25, (enemyConfig.size || 1) * 1.4);
    const pos = findSafeSpawn(basePos, enemyRadius);
    if (!pos) {
      // Nunca spawnar inimigo inválido dentro de obstáculo.
      continue;
    }
    spawnedEnemyFootprints.push({ pos: pos.clone(), radius: enemyRadius });
    
    enemies.push({
      id: `enemy-${i}`,
      position: pos,
      rotation: Math.random() * Math.PI * 2,
      health: enemyConfig.health,
      maxHealth: enemyConfig.health,
      color: enemyConfig.color,
      isPlayer: false,
      isAlive: true,
      name: getEnemyName(enemyConfig.type, i),
      lastFireTime: 0,
      enemyType: enemyConfig.type,
      speed: enemyConfig.speed,
      damage: enemyConfig.damage,
      fireRate: enemyConfig.fireRate,
      shotCount: enemyConfig.shotCount,
      size: enemyConfig.size,
      aiLevel: enemyConfig.aiLevel,
      explodesOnDeath: enemyConfig.explodesOnDeath,
    } as TankData & { enemyType: string; speed: number; damage: number; fireRate: number; shotCount: number; size: number; aiLevel: number });
  }
  
  return enemies;
}

export function getRandomTerrain(): TerrainConfig {
  const themes = Object.keys(TERRAIN_THEMES) as TerrainTheme[];
  const randomIndex = Math.floor(Math.random() * themes.length);
  return TERRAIN_THEMES[themes[randomIndex]];
}

function getEnemyName(type: string, index: number): string {
  const names: Record<string, string[]> = {
    normal: ["Soldado", "Recruta", "Guarda", "Patrulha"],
    heavy: ["Tanque Pesado", "Blindado", "Colosso", "Destruidor"],
    fast: ["Veloz", "Rapido", "Relampago", "Furacao"],
    sniper: ["Atirador", "Precisao", "Franco-atirador", "Mira"],
    boss: ["Comandante", "General", "Chefe", "Lider"],
  };
  const typeNames = names[type] || names.normal;
  return typeNames[index % typeNames.length];
}

export type WaveDifficulty = "easy" | "medium" | "hard" | "veryHard" | "extreme" | "insane" | "impossible";

/**
 * Retorna a chave de dificuldade da wave (para usar com i18n)
 * Use com t(`game.difficulty${capitalize(key)}`) no componente
 */
export function getWaveDifficultyKey(wave: number): WaveDifficulty {
  if (wave <= 3) return "easy";
  if (wave <= 6) return "medium";
  if (wave <= 10) return "hard";
  if (wave <= 15) return "veryHard";
  if (wave <= 20) return "extreme";
  if (wave <= 30) return "insane";
  return "impossible";
}

/**
 * @deprecated Use getWaveDifficultyKey com i18n
 * Retorna nome da dificuldade (legacy, para compatibilidade)
 */
export function getWaveDifficultyName(wave: number): string {
  const key = getWaveDifficultyKey(wave);
  const names: Record<WaveDifficulty, string> = {
    easy: "Fácil",
    medium: "Médio", 
    hard: "Difícil",
    veryHard: "Muito Difícil",
    extreme: "Extremo",
    insane: "Insano",
    impossible: "Impossível",
  };
  return names[key];
}

export function getWaveReward(wave: number, kills: number): number {
  const baseReward = 10 + wave * 5;
  const killBonus = kills * 10;
  const waveBonus = Math.floor(wave / 5) * 25;
  return baseReward + killBonus + waveBonus;
}
